import React from 'react';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const DEFAULT_STEPS = [
    { key: 'preprocessing_documents', label: 'Preparing uploaded documents' },
    { key: 'gathering_data', label: 'Gathering project data' },
    { key: 'passing_to_ai', label: 'Preparing AI inputs' },
    { key: 'gpt_generation', label: 'Generating report sections' },
    { key: 'formatting', label: 'Formatting the report' },
    { key: 'done', label: 'Ready for surveyor review' }
];

const SECTION_LABELS = {
    introduction: 'Introduction', location_site: 'Location and site', 'location_site.location': 'Location',
    'location_site.existing_site': 'Existing site', 'location_site.proposed_development': 'Proposed development',
    planning: 'Statutory approvals', surveys: 'Surveys and drawings', procurement: 'Procurement and contractor',
    professional_team: 'Professional team', programme: 'Construction programme', costs: 'Construction costs',
    risk_summary: 'Risk summary', executive_summary: 'Executive summary', site_visit_progress: 'Site visit and progress',
    drawdown: 'Drawdown', statutory: 'Statutory authorities', design_quality: 'Design and quality',
    procurement_contract: 'Procurement and contract', team_contractor: 'Professional team and contractor', insurances: 'Insurances'
};

export function getReportGenerationDetail(status) {
    if (status?.stage === 'gemini_style') return 'Refining report language';
    if (status?.stage === 'claude_validation') return 'Checking report consistency';
    const section = String(status?.message || '').match(/\bsection=([a-z0-9_.-]+)/i)?.[1];
    if (section === 'consistency_check') return 'Checking report consistency';
    return SECTION_LABELS[section] ? `Current section: ${SECTION_LABELS[section]}` : '';
}

export default function ReportGenerationProgress({ generating = false, stage = '', stageLabel = '', detail = '', steps = DEFAULT_STEPS }) {
    if (!generating && !stage) return null;
    const failed = stage === 'error';
    const complete = stage === 'done';
    const index = Math.max(0, steps.findIndex((step) => step.key === stage));
    const value = complete ? 100 : index * 100 / Math.max(1, steps.length - 1);
    const label = failed ? 'Report generation stopped' : complete ? 'Ready for surveyor review' : stageLabel || steps[index]?.label || 'Preparing report';
    return <Box component="section" aria-label="Report generation progress" aria-busy={generating && !complete && !failed} sx={{ p: 2, minWidth: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>Report generation</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>{complete ? 'Complete' : failed ? 'Stopped' : `Stage ${index + 1} of ${steps.length}`}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={failed ? 0 : value} color={failed ? 'error' : complete ? 'success' : 'primary'}
            aria-label="Workflow stage progress" aria-valuetext={complete ? 'Report ready for review' : failed ? 'Generation stopped' : `Stage ${index + 1} of ${steps.length}: ${label}`}
            sx={{ height: 6, borderRadius: 0 }} />
        <Box role="status" aria-live="polite" aria-atomic="true" sx={{ mt: 1.25, minHeight: 40, overflowWrap: 'anywhere' }}>
            <Typography variant="body2" sx={{ fontWeight: 650 }}>{label}</Typography>
            {detail && !complete && !failed ? <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{detail}</Typography> : null}
        </Box>
        {generating && !complete && !failed ? <LinearProgress aria-label="Current stage processing" sx={{ mt: 0.75, height: 3, borderRadius: 0 }} /> : null}
        <Box component="ol" sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(3,minmax(0,1fr))' },
            gap: 1, listStyle: 'none', m: 0, p: 0, mt: 1.5 }}>
            {steps.map((step, stepIndex) => {
                const done = complete || (!failed && stepIndex < index);
                const active = !complete && !failed && stepIndex === index;
                const Icon = done ? CheckCircleOutlineIcon : RadioButtonUncheckedIcon;
                return <Stack component="li" key={step.key} direction="row" spacing={0.75} alignItems="flex-start" aria-current={active ? 'step' : undefined}
                    sx={{ minWidth: 0, color: done ? 'success.main' : active ? 'text.primary' : 'text.secondary' }}>
                    <Icon aria-hidden="true" sx={{ fontSize: 17, flexShrink: 0, mt: 0.1 }} />
                    <Typography variant="caption" sx={{ fontWeight: active ? 750 : 500, overflowWrap: 'anywhere' }}>{step.label}</Typography>
                </Stack>;
            })}
        </Box>
    </Box>;
}